/* eslint-disable object-property-newline */

import Combobox from '@components/combobox';
import smoothScroll from '@plugins/smooth-scroll';

/**
 * ROI Calculator (Combobox + preloaded institution data)
 *
 * Expects markup rendered by Twig:
 * - form.js-roi
 * - input.js-roi-institution (role="combobox")
 * - div.js-roi-listbox (role="listbox")
 * - script.js-roi-data[type="application/json"] containing an array of rows:
 *   [name, enrollment, retention, tuition, improved_retention_rate, additional_students, estimated_return]
 */
class RoiCalculator {
  /**
   * @param {HTMLElement} el Root block element
   */
  constructor(el) {
    this.$root = $(el);
    this.$form = this.$root.find('.js-roi');

    if (!this.$form.length) {
      return;
    }

    this.$inst = this.$form.find('.js-roi-institution');
    this.$listbox = this.$form.find('.js-roi-listbox');

    this.$fte = this.$form.find('.js-roi-fte');
    this.$rate = this.$form.find('.js-roi-rate');
    this.$tuition = this.$form.find('.js-roi-tuition');
    this.$submit = this.$form.find('.js-roi-submit');

    this.$errInst = this.$form.find('#roi-institution-error');
    this.$errFte = this.$form.find('#roi-fte-error');
    this.$errRate = this.$form.find('#roi-rate-error');
    this.$errTuition = this.$form.find('#roi-tuition-error');

    this.$resultsTitle = this.$root.find('.js-roi-results-title');
    this.$results = this.$root.find('#roi-results');
    this.$outImproved = this.$root.find('.js-roi-out-improved');
    this.$outStudents = this.$root.find('.js-roi-out-students');
    this.$outReturn = this.$root.find('.js-roi-out-return');

    this.$downloadWrap = this.$root.find('.js-roi-download-wrap');
    this.$downloadPdf = this.$root.find('.js-roi-download-pdf');
    this.$email = this.$root.find('.js-roi-email');
    this.$errEmail = this.$root.find('.js-roi-email-error');
    this.$errDownloadPdf = this.$root.find('.js-roi-download-error');
    this.$downloadSuccess = this.$root.find('.js-roi-download-success');
    this.pdfEndpoint = String(this.$root.data('roiPdfEndpoint') || '');
    this.leadEndpoint = String(this.$root.data('roiLeadEndpoint') || '');

    this.rows = this.readRows();
    this.byName = this.indexByName(this.rows);
    this.names = this.rows.map((r) => r[0]);

    this.combobox = null;

    this.numericFields = [
      { $input: this.$fte, $err: this.$errFte, label: 'Student FTE', opts: { min: 1 }, clampMax: null },
      { $input: this.$rate, $err: this.$errRate, label: 'Current Retention Rate', opts: { min: 0, max: 100 }, clampMax: 100 },
      { $input: this.$tuition, $err: this.$errTuition, label: 'Per-Student Value', opts: { min: 1 }, clampMax: null }
    ];

    // Number formatter: whole dollars, US formatting
    this.usd = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    });

    this.install();
    this.restoreStateFromSoftRefresh();
  }

  /**
   * Parse JSON from <script type="application/json" class="js-roi-data">.
   * @return {Array<Array<any>>}
   */
  readRows() {
    const raw = this.$root.find('.js-roi-data').first().text();
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Build case-sensitive name -> row map.
   * @param {Array<Array<any>>} rows
   * @return {Map<string, Array<any>>}
   */
  indexByName(rows) {
    const map = new Map();

    rows.forEach((r) => {
      if (!Array.isArray(r) || typeof r[0] !== 'string') {
        return;
      }
      map.set(r[0], r);
    });

    return map;
  }

  /**
   * Bind events and initial ARIA setup.
   * @return {void}
   */
  install() {
    // Ensure combobox controls listbox
    const listboxId = this.$listbox.attr('id');
    if (listboxId) {
      this.$inst.attr('aria-controls', listboxId);
    }

    // Institution combobox (shared component)
    this.combobox = new Combobox({
      input: this.$inst[0],
      listbox: this.$listbox[0],
      minQueryLen: 1,
      debounceMs: 60,
      ariaLabel: 'Institutions',
      getItems: async (query) => {
        const q = String(query || '').trim();
        if (!q) {
          return [];
        }

        const limit = 25;
        return Combobox.rankMatches(this.names, q, limit);
      },
      onInput: () => {
        // typing clears selection guarantee
        this.clearInstitutionError();
        this.clearOutputs();

        const q = String(this.$inst.val() || '');

        if (this.byName.has(q)) {
          this.applyInstitution(q);
          // when the user has typed an exact match, treat it as selected
          this.combobox.setSelectedValue(q);
        } else {
          this.lockNumericFieldsOnly();
          this.setDisabled(this.$submit, true);
        }
      },
      onSelect: (value) => {
        // Selecting from list means numeric fields are now populated
        this.clearOutputs();
        this.applyInstitution(String(value || ''));
      },
      onBlurInvalid: () => {
        const name = String(this.$inst.val() || '').trim();

        if (!name) {
          this.lockNumericFieldsOnly();
          this.setDisabled(this.$submit, true);
          this.clearOutputs();
          return;
        }

        if (!this.byName.has(name)) {
          this.setError(this.$inst, this.$errInst, 'Please select your institution from the list.');
          this.lockNumericFieldsOnly();
          this.setDisabled(this.$submit, true);
          this.clearOutputs();
          return;
        }

        // valid exact match
        this.clearInstitutionError();
        this.combobox.setSelectedValue(name);
      }
    });

    // Strict whole-number typing (mobile-friendly)
    this.numericFields.forEach(({ $input, clampMax }) => {
      $input.on('input', () => {
        const digits = this.stripToWholeNumberString($input.val());
        if (String($input.val()) !== digits) {
          $input.val(digits);
        }

        if (typeof clampMax === 'number') {
          const n = this.toInt(digits);
          if (digits && n > clampMax) {
            $input.val(String(clampMax));
          }
        }

        this.clearFieldError($input);
        this.maybeToggleSubmit();
      });
    });

    // Normalize on blur
    this.numericFields.forEach(({ $input, $err, label, opts }) => {
      $input.on('blur', () => {
        this.validateWholeNumberField($input, $err, label, opts);
        this.maybeToggleSubmit();
      });
    });

    // Submit
    this.$form.on('submit', (e) => {
      e.preventDefault();
      this.clearAllErrors();

      if (!this.canSubmit()) {
        this.setDisabled(this.$submit, true);
        return;
      }

      const name = String(this.$inst.val() || '');
      const enrollment = this.toInt(this.$fte.val());
      const retention = this.clamp(this.toInt(this.$rate.val()), 0, 100);
      const tuition = this.toInt(this.$tuition.val());

      const out = this.compute(enrollment, retention, tuition);

      this.$resultsTitle.text(`Results for ${name}`);
      this.setDd(this.$outImproved, this.formatImprovedRate(retention, out.improved_retention_rate));
      this.setDd(this.$outStudents, this.formatStudents(out.additional_students));
      this.setDd(this.$outReturn, this.formatUSD(out.estimated_return));
      this.setDownloadError('');
      this.setEmailError('');
      this.setDownloadSuccess(false);

      // Show email gate + download once results exist
      if (this.$downloadWrap && this.$downloadWrap.length) {
        this.$downloadWrap.prop('hidden', false);
      }

      // Smooth scroll to results after submit
      smoothScroll(this.$results);
    });

    // Download PDF
    if (this.$downloadPdf && this.$downloadPdf.length) {
      this.$downloadPdf.on('click', async () => {
        const name = String(this.$inst.val() || '').trim();

        // Only allow download when we have valid current results
        if (!name || !this.byName.has(name) || !this.canSubmit()) {
          return;
        }

        if (!this.pdfEndpoint) {
          return;
        }
        if (!this.leadEndpoint) {
          this.setDownloadError('Lead capture endpoint is unavailable.');
          return;
        }

        const email = String(this.$email.val() || '').trim();
        if (!this.isValidEmail(email)) {
          this.setEmailError('Please enter a valid email address.');
          return;
        }
        this.setEmailError('');

        const enrollment = this.toInt(this.$fte.val());
        const retention = this.clamp(this.toInt(this.$rate.val()), 0, 100);
        const tuition = this.toInt(this.$tuition.val());
        const payload = {
          institution: name,
          fte: enrollment,
          retention,
          per_student_value: tuition
        };

        try {
          this.setDownloadError('');
          this.$downloadPdf.prop('disabled', true).attr('aria-disabled', 'true');

          const leadRes = await fetch(this.leadEndpoint, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email,
              institution: name,
              fte: enrollment,
              retention,
              per_student_value: tuition
            })
          });

          if (!leadRes.ok) {
            let message = 'Unable to submit email right now. Please try again.';

            try {
              const leadErrJson = await leadRes.json();
              if (leadErrJson && typeof leadErrJson.error === 'string' && leadErrJson.error.trim()) {
                message = leadErrJson.error.trim();
              }
            } catch (e) {
              // non-JSON error response
            }

            this.setDownloadError(message);
            return;
          }

          const res = await fetch(this.pdfEndpoint, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (!res.ok) {
            let message = 'Unable to download right now. Please try again.';

            try {
              const errJson = await res.json();
              if (errJson && typeof errJson.error === 'string' && errJson.error.trim()) {
                message = errJson.error.trim();
              }
            } catch (e) {
              // non-JSON error response
            }

            this.setDownloadError(message);
            return;
          }

          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);

          const safeName = name.replace(/[^a-z0-9\-\s]/gi, '').trim().replace(/\s+/g, '-');
          const filename = safeName ? `packback-roi-${safeName}.pdf` : 'packback-roi.pdf';

          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();

          window.URL.revokeObjectURL(url);
          if (this.$downloadWrap && this.$downloadWrap.length) {
            this.$downloadWrap.prop('hidden', true);
          }
          this.setDownloadSuccess(true);
        } catch (err) {
          this.setDownloadError('There was a network issue while downloading. Please try again.');
        } finally {
          this.$downloadPdf.prop('disabled', false).attr('aria-disabled', 'false');
        }
      });
    }

    // (Combobox handles option click and outside click)
  }


  /**
   * Soft-refresh behavior:
   * - Do not clear fields on load
   * - If institution is filled and valid, unlock fields + enable submit if fields have values
   * @return {void}
   */
  restoreStateFromSoftRefresh() {
    const name = String(this.$inst.val() || '');

    this.clearOutputs();

    if (name && this.byName.has(name)) {
      this.unlockEditableFields();
      this.maybeToggleSubmit();

      if (this.combobox) {
        this.combobox.setSelectedValue(name);
      }

      return;
    }

    this.lockNumericFieldsOnly();
    this.setDisabled(this.$submit, true);
  }


  /**
   * Select an institution by name (case-sensitive exact match).
   * @param {string} name
   * @return {void}
   */
  selectInstitution(name) {
    // Backwards compat: if any code calls this, route through the shared combobox
    if (this.combobox) {
      this.combobox.select(String(name || ''));
      return;
    }

    this.$inst.val(name);
    this.applyInstitution(name);
  }

  /**
   * Populate fields from row and enable submit.
   * @param {string} name
   * @return {void}
   */
  applyInstitution(name) {
    const row = this.byName.get(name);
    if (!row) {
      return;
    }

    this.fillFromRow(row);
    this.clearAllErrors();
    this.unlockEditableFields();
    this.maybeToggleSubmit();
  }

  /**
   * Fill the editable inputs from a row.
   * Row format:
   * [name, enrollment, retention, tuition, improved, additional_students, estimated_return]
   * @param {Array<any>} row
   * @return {void}
   */
  fillFromRow(row) {
    const enrollment = Number.isFinite(row[1]) ? row[1] : this.toInt(row[1]);
    const retention = Number.isFinite(row[2]) ? row[2] : this.toInt(row[2]);
    const tuition = Number.isFinite(row[3]) ? row[3] : this.toInt(row[3]);

    this.$fte.val(String(Math.max(0, Math.trunc(enrollment))));
    this.$rate.val(String(this.clamp(Math.trunc(retention), 0, 100)));
    this.$tuition.val(String(Math.max(0, Math.trunc(tuition))));
  }

  /**
   * Enable/disable readonly state for the numeric inputs.
   * @param {jQuery} $input
   * @param {boolean} isReadonly
   * @return {void}
   */
  setReadonly($input, isReadonly) {
    const ro = Boolean(isReadonly);
    $input.prop('readonly', ro);
    $input.attr('aria-readonly', ro ? 'true' : 'false');
  }

  /**
   * Enable/disable the submit button.
   * @param {jQuery} $btn
   * @param {boolean} isDisabled
   * @return {void}
   */
  setDisabled($btn, isDisabled) {
    const dis = Boolean(isDisabled);
    $btn.prop('disabled', dis);
    $btn.attr('aria-disabled', dis ? 'true' : 'false');
  }


  /**
   * Set an error message on a field.
   * @param {jQuery} $input
   * @param {jQuery} $err
   * @param {string} message
   * @return {void}
   */
  setError($input, $err, message) {
    const text = String(message || '').trim();
    $input.attr('aria-invalid', text ? 'true' : 'false');

    if (!text) {
      $err.prop('hidden', true).text('');
      return;
    }

    $err.text(text).prop('hidden', false);
  }

  /**
   * Clear institution error.
   * @return {void}
   */
  clearInstitutionError() {
    this.setError(this.$inst, this.$errInst, '');
  }

  /**
   * Clear the error for a numeric input.
   * @param {jQuery} $input
   * @return {void}
   */
  clearFieldError($input) {
    if ($input.is(this.$fte)) {
      this.setError(this.$fte, this.$errFte, '');
    } else if ($input.is(this.$rate)) {
      this.setError(this.$rate, this.$errRate, '');
    } else if ($input.is(this.$tuition)) {
      this.setError(this.$tuition, this.$errTuition, '');
    }
  }

  /**
   * Clear all field errors.
   * @return {void}
   */
  clearAllErrors() {
    this.$form.find('[aria-invalid]').attr('aria-invalid', 'false');
    this.$form.find('.field-error').prop('hidden', true).text('');
  }

  /**
   * Lock the numeric fields (do not clear values).
   * @return {void}
   */
  lockNumericFieldsOnly() {
    this.setReadonly(this.$fte, true);
    this.setReadonly(this.$rate, true);
    this.setReadonly(this.$tuition, true);
  }

  /**
   * Unlock number inputs for edits after selecting an institution.
   * @return {void}
   */
  unlockEditableFields() {
    this.setReadonly(this.$fte, false);
    this.setReadonly(this.$rate, false);
    this.setReadonly(this.$tuition, false);
  }

  /**
   * Strip to digits only (whole numbers only).
   * @param {any} value
   * @return {string}
   */
  stripToWholeNumberString(value) {
    const s = String(value || '');
    return s.replace(/[^\d]/g, '');
  }

  /**
   * Parse whole-number digit string into int.
   * @param {any} value
   * @return {number}
   */
  toInt(value) {
    const digits = this.stripToWholeNumberString(value);
    if (!digits) {
      return 0;
    }
    return parseInt(digits, 10);
  }

  /**
   * Clamp number to [min, max].
   * @param {number} n
   * @param {number} min
   * @param {number} max
   * @return {number}
   */
  clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  /**
   * Improved retention delta in percentage points based on current retention.
   * @param {number} retentionWhole
   * @return {number}
   */
  improvedRetentionDeltaPoints(retentionWhole) {
    if (retentionWhole < 50) {
      return 2.0;
    }
    if (retentionWhole < 70) {
      return 1.0;
    }
    return 0.5;
  }

  /**
   * Compute results from the three numeric inputs.
   * improved_retention_rate is a float; other outputs are whole numbers.
   * @param {number} enrollmentWhole
   * @param {number} retentionWhole
   * @param {number} tuitionWhole
   * @return {{improved_retention_rate:number, additional_students:number, estimated_return:number}}
   */
  compute(enrollmentWhole, retentionWhole, tuitionWhole) {
    const improvedDelta = this.improvedRetentionDeltaPoints(retentionWhole);
    const improved = retentionWhole + improvedDelta;
    const additional = Math.round(enrollmentWhole * (improvedDelta / 100));
    const estimated = Math.round(additional * tuitionWhole);

    return {
      improved_retention_rate: improved,
      additional_students: additional,
      estimated_return: estimated
    };
  }

  /**
   * Validate institution as an exact case-sensitive match.
   * @return {boolean}
   */
  validateInstitution() {
    const name = String(this.$inst.val() || '');

    if (!name || !this.byName.has(name)) {
      this.setError(this.$inst, this.$errInst, 'Please select your institution from the list.');
      return false;
    }

    this.clearInstitutionError();
    return true;
  }

  /**
   * Validate a whole-number field with optional min/max.
   * @param {jQuery} $input
   * @param {jQuery} $err
   * @param {string} label
   * @param {{min?:number, max?:number}} opts
   * @return {boolean}
   */
  validateWholeNumberField($input, $err, label, opts) {
    const raw = String($input.val() || '');
    const digits = this.stripToWholeNumberString(raw);

    if (!digits) {
      this.setError($input, $err, `Please enter ${label}.`);
      return false;
    }

    const n = parseInt(digits, 10);

    if (!Number.isFinite(n)) {
      this.setError($input, $err, `Please enter ${label}.`);
      return false;
    }

    if (opts && typeof opts.min === 'number' && n < opts.min) {
      this.setError($input, $err, `${label} must be at least ${opts.min}.`);
      return false;
    }

    if (opts && typeof opts.max === 'number' && n > opts.max) {
      this.setError($input, $err, `${label} must be ${opts.max} or less.`);
      return false;
    }

    this.setError($input, $err, '');
    $input.val(String(n));
    return true;
  }

  /**
   * Determine if form can submit.
   * @return {boolean}
   */
  canSubmit() {
    if (!this.validateInstitution()) {
      return false;
    }

    return this.numericFields.every(({ $input, $err, label, opts }) => this.validateWholeNumberField($input, $err, label, opts));
  }

  /**
   * Enable submit only when institution is valid and all three fields have values.
   * @return {void}
   */
  maybeToggleSubmit() {
    const name = String(this.$inst.val() || '');

    if (!name || !this.byName.has(name)) {
      this.setDisabled(this.$submit, true);
      return;
    }

    const ready = this.numericFields.every(({ $input }) => Boolean(this.stripToWholeNumberString($input.val())));
    this.setDisabled(this.$submit, !ready);
  }

  /**
   * Format: "old% → improved%"
   * old is whole percent, improved can be decimal (.5).
   * @param {number} oldWhole
   * @param {number} improved
   * @return {string}
   */
  formatImprovedRate(oldWhole, improved) {
    const oldText = `${Math.round(oldWhole)}%`;
    const improvedRounded = Math.round(improved * 10) / 10;
    const improvedText = Number.isInteger(improvedRounded) ? `${improvedRounded}%` : `${improvedRounded.toFixed(1)}%`;
    return `${oldText} \u2192 ${improvedText}`;
  }

  /**
   * Format students with pluralization.
   * @param {number} n
   * @return {string}
   */
  formatStudents(n) {
    const count = Math.max(0, Math.round(n));
    return `${count} ${count === 1 ? 'Student' : 'Students'}`;
  }

  /**
   * Format USD as whole dollars.
   * @param {number} n
   * @return {string}
   */
  formatUSD(n) {
    const value = Math.max(0, Math.round(n));
    return this.usd.format(value);
  }

  /**
   * Set a <dd> value and toggle its visibility.
   * Uses .text() to keep outputs XSS-safe.
   * @param {jQuery} $dd
   * @param {string} value
   * @return {void}
   */
  setDd($dd, value) {
    const text = String(value || '');
    const show = Boolean(text);

    $dd
      .text(text)
      .prop('hidden', !show)
      .attr('aria-hidden', show ? 'false' : 'true');
  }

  /**
   * Clear all result values (keep results section visible).
   * @return {void}
   */
  clearOutputs() {
    this.setDd(this.$outImproved, '');
    this.setDd(this.$outStudents, '');
    this.setDd(this.$outReturn, '');
    this.setDownloadError('');
    this.setEmailError('');
    this.setDownloadSuccess(false);

    if (this.$downloadWrap && this.$downloadWrap.length) {
      this.$downloadWrap.prop('hidden', true);
    }
  }

  /**
   * Validate email format.
   * @param {string} email
   * @return {boolean}
   */
  isValidEmail(email) {
    const value = String(email || '').trim();
    if (!value) {
      return false;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  /**
   * Show/hide the email field error message.
   * @param {string} message
   * @return {void}
   */
  setEmailError(message) {
    if (!this.$email || !this.$email.length || !this.$errEmail || !this.$errEmail.length) {
      return;
    }

    const text = String(message || '').trim();
    this.$email.attr('aria-invalid', text !== '' ? 'true' : 'false');
    this.$errEmail
      .text(text)
      .prop('hidden', text === '');
  }

  /**
   * Toggle post-download success message.
   * @param {boolean} show
   * @return {void}
   */
  setDownloadSuccess(show) {
    if (!this.$downloadSuccess || !this.$downloadSuccess.length) {
      return;
    }

    const visible = Boolean(show);
    this.$downloadSuccess.prop('hidden', !visible);
  }

  /**
   * Show/hide the PDF download error message.
   * @param {string} message
   * @return {void}
   */
  setDownloadError(message) {
    if (!this.$errDownloadPdf || !this.$errDownloadPdf.length) {
      return;
    }

    const text = String(message || '').trim();
    this.$errDownloadPdf
      .text(text)
      .prop('hidden', text === '');
  }

}

export default (el) => {
  // Don't run in wp-admin/editor
  if (!$('html').hasClass('frontend')) {
    return;
  }

  new RoiCalculator(el);
};
